/**
 * HTTP implementation of ApiClient. Cookie session (credentials: "include"); every mutation carries
 * `x-contentops: 1` (CSRF guard the API checks); responses are zod-parsed; every failure becomes an ApiError.
 */
import {
  AttachCoverRequest, AttachSeriesCoverRequest, ChooseCoverRequest, ChooseSeriesCoverRequest, NotificationSettings, PushSubscriptionRequest, UpdatePublishingSettings,
  CreateAccountRequest, CreateJobRequest, DecisionAnswer, LearningDecision, OwnerMessage, PlanRevisionRequest, PlanSelection, PublicationMetadata, PublishPlanRequest, RescheduleRequest, ReviewAction,
  UpdateAccountRequest, UpsertQueueRequest,
} from "@contentops/contract";
import type { z } from "zod";
import type { ApiClient, EventSourceLike } from "./client";
import { ApiError, codeForStatus } from "./errors";
import * as T from "./types";

export type HttpOptions = {
  baseUrl?: string;
  fetch?: typeof fetch;
  timeoutMs?: number;
  /** Called on any 401 so the shell can drop back to the sign-in screen. */
  onUnauthorized?: () => void;
  eventSource?: (url: string) => EventSourceLike;
  /** Full-page navigation (OAuth connect). Defaults to window.location.assign; injectable for tests. */
  assign?: (url: string) => void;
};

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export function createHttpApi(opts: HttpOptions = {}): ApiClient {
  const base = opts.baseUrl ?? "";
  const doFetch = opts.fetch ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  const timeoutMs = opts.timeoutMs ?? 30_000;

  async function request<S extends z.ZodType>(method: Method, path: string, schema: S | null, body?: unknown): Promise<z.output<S>> {
    const headers: Record<string, string> = { accept: "application/json" };
    if (method !== "GET") headers["x-contentops"] = "1";
    if (body !== undefined) headers["content-type"] = "application/json";
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    let res: Response;
    try {
      res = await doFetch(base + path, {
        method, headers, credentials: "include", signal: ctrl.signal,
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch (err) {
      if (ctrl.signal.aborted) throw new ApiError("timeout", "Request timed out", 0);
      throw new ApiError("network", err instanceof Error ? err.message : "Network error", 0);
    } finally {
      clearTimeout(timer);
    }
    return handle(res, schema);
  }

  async function handle<S extends z.ZodType>(res: Response, schema: S | null): Promise<z.output<S>> {
    const text = await res.text().catch(() => "");
    let json: unknown = undefined;
    if (text) {
      try { json = JSON.parse(text); } catch { json = undefined; }
    }
    if (!res.ok) {
      if (res.status === 401) opts.onUnauthorized?.();
      const errBody = (json ?? {}) as { error?: unknown; message?: unknown; details?: unknown };
      const nested = typeof errBody.error === "object" && errBody.error ? (errBody.error as { message?: unknown; details?: unknown }) : undefined;
      const message = String(nested?.message ?? errBody.message ?? (typeof errBody.error === "string" ? errBody.error : "") ?? "") || res.statusText || `HTTP ${res.status}`;
      let details = nested?.details ?? errBody.details;
      if (res.status === 429) {
        // "Try again at …": the API sends { retry_at } (top level, under error, or in details); Retry-After is the fallback.
        const retryAt = retryAtFrom(json) ?? retryAfterHeader(res.headers.get("retry-after"));
        if (retryAt) details = { ...(typeof details === "object" && details ? details : {}), retry_at: retryAt };
      }
      throw new ApiError(codeForStatus(res.status), message, res.status, details);
    }
    if (!schema) return undefined as z.output<S>;
    if (json === undefined) throw new ApiError("bad_response", "Empty or non-JSON response", res.status);
    const parsed = schema.safeParse(json);
    if (!parsed.success) throw new ApiError("bad_response", "Response did not match the expected shape", res.status, parsed.error.issues);
    return parsed.data;
  }

  /** Validates a request body against the contract before it leaves the phone (fast, precise errors). */
  function check<S extends z.ZodType>(schema: S, value: unknown): z.output<S> {
    const r = schema.safeParse(value);
    if (!r.success) throw new ApiError("validation", r.error.issues[0]?.message ?? "Invalid input", 0, r.error.issues);
    return r.data;
  }
  /** Validates but sends the caller's value unchanged — for PATCH bodies, where zod 4's `.partial()` would fill in defaults and overwrite fields. */
  function validateOnly<V>(schema: z.ZodType, value: V): V {
    check(schema, value);
    return value;
  }

  const q = (params: Record<string, string | number | undefined>) => {
    const s = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== "") s.set(k, String(v));
    const str = s.toString();
    return str ? `?${str}` : "";
  };
  const enc = encodeURIComponent;

  return {
    mode: "http",
    me: async () => (await request("GET", "/api/me", T.MeResponse)).user,
    logout: async () => { await request("POST", "/auth/logout", null); },
    health: () => request("GET", "/api/health", T.Health),
    dashboard: () => request("GET", "/api/dashboard", T.Dashboard),
    categories: async () => (await request("GET", "/api/categories", T.CategoriesResponse)).categories,
    languages: async () => (await request("GET", "/api/languages", T.LanguagesResponse)).languages,

    upload: (file, o = {}) =>
      new Promise((resolve, reject) => {
        // XHR (not fetch) because upload progress matters on a phone with a large SRT or media file.
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${base}/api/uploads`);
        xhr.withCredentials = true;
        xhr.setRequestHeader("x-contentops", "1");
        xhr.setRequestHeader("accept", "application/json");
        xhr.upload.onprogress = (e) => { if (e.lengthComputable) o.onProgress?.(e.loaded / e.total); };
        xhr.onerror = () => reject(new ApiError("network", "Upload failed — check your connection", 0));
        xhr.onabort = () => reject(new ApiError("aborted", "Upload cancelled", 0));
        xhr.onload = () => {
          const res = new Response(xhr.responseText || null, { status: xhr.status, statusText: xhr.statusText });
          handle(res, T.UploadResponse).then(resolve, reject);
        };
        o.signal?.addEventListener("abort", () => xhr.abort());
        const form = new FormData();
        if (o.kind) form.append("kind", o.kind);
        form.append("file", file, file.name);
        xhr.send(form);
      }),
    createJob: (req) => request("POST", "/api/jobs", T.CreateJobResponse, check(CreateJobRequest, req)),

    listSeries: async () => (await request("GET", "/api/series", T.SeriesListResponse)).series,
    getSeries: (id) => request("GET", `/api/series/${enc(id)}`, T.SeriesDetail),
    approvePlan: async (id, sel) => { await request("POST", `/api/series/${enc(id)}/plan/approve`, null, check(PlanSelection, sel)); },
    revisePlan: async (id, req) => request("POST", `/api/series/${enc(id)}/plan/revise`, T.PlanRevisionResponse, check(PlanRevisionRequest, req)),
    listMessages: async (id, after) => (await request("GET", `/api/series/${enc(id)}/messages${q({ after })}`, T.MessagesResponse)).messages,
    sendMessage: async (id, body) => (await request("POST", `/api/series/${enc(id)}/messages`, T.MessageResponse, check(OwnerMessage, { body }))).message,
    interrupt: async (id) => { await request("POST", `/api/series/${enc(id)}/interrupt`, null, {}); },

    listDecisions: async (status = "open") => (await request("GET", `/api/decisions${q({ status })}`, T.DecisionsResponse)).decisions,
    answerDecision: async (id, a) => { await request("POST", `/api/decisions/${enc(id)}/answer`, null, check(DecisionAnswer, a)); },

    studioSettings: () => request("GET", "/api/settings/studio", T.StudioSettings),
    // The PUT response is not relied on (the shape may be the settings or a bare ok); the screen keeps what it sent.
    updateStudioSettings: async (s) => { const body = check(T.StudioSettings, s); await request("PUT", "/api/settings/studio", null, body); return body; },
    listLearnings: async (status) => (await request("GET", `/api/learnings${q({ status })}`, T.LearningsResponse)).learnings,
    approveLearning: async (id, d) => request("POST", `/api/learnings/${enc(id)}/approve`, T.Learning, check(LearningDecision, d)),
    declineLearning: async (id, d) => request("POST", `/api/learnings/${enc(id)}/decline`, T.Learning, check(LearningDecision, d)),

    getVersion: (id) => request("GET", `/api/versions/${enc(id)}`, T.VersionDetail),
    reviewVersion: async (id, a) => { await request("POST", `/api/versions/${enc(id)}/review`, null, check(ReviewAction, a)); },
    addFeedback: async (id, fb) => (await request("POST", `/api/versions/${enc(id)}/feedback`, T.FeedbackResponse, fb)).feedback,
    artifactUrl: (id) => `${base}/api/artifacts/${enc(id)}/content`,
    versionCovers: (id) => request("GET", `/api/versions/${enc(id)}/covers`, T.VersionCovers),
    attachCover: async (id, artifactId, label) => request("POST", `/api/versions/${enc(id)}/covers`, T.VersionCovers, check(AttachCoverRequest, { artifact_id: artifactId, ...(label ? { label } : {}) })),
    chooseCover: async (id, artifactId) => request("PUT", `/api/versions/${enc(id)}/cover`, T.VersionCovers, check(ChooseCoverRequest, { artifact_id: artifactId })),
    seriesCovers: (id) => request("GET", `/api/series/${enc(id)}/covers`, T.SeriesCovers),
    attachSeriesCover: async (id, artifactId, label) => request("POST", `/api/series/${enc(id)}/covers`, T.SeriesCovers, check(AttachSeriesCoverRequest, { artifact_id: artifactId, ...(label ? { label } : {}) })),
    chooseSeriesCover: async (id, artifactId) => request("PUT", `/api/series/${enc(id)}/cover`, T.SeriesCovers, check(ChooseSeriesCoverRequest, { artifact_id: artifactId })),

    // Action responses (PublicationView / QueueView) are not parsed: the screens refetch through SSE invalidation anyway.
    listPublications: async (f = {}) => (await request("GET", `/api/publications${q({ state: f.state, account_id: f.account_id, series_id: f.series_id, from: f.from, to: f.to, q: f.q, order: "asc", limit: 200 })}`, T.PublicationsPage)).publications,
    pagePublications: (p) => request("GET", `/api/publications${q({ state: p.state, account_id: p.account_id, series_id: p.series_id, from: p.from, to: p.to, q: p.q, cursor: p.cursor, limit: p.limit ?? 50, order: p.order ?? "desc" })}`, T.PublicationsPage),
    getPublication: (id) => request("GET", `/api/publications/${enc(id)}`, T.Publication),
    publicationHistory: (id) => request("GET", `/api/publications/${enc(id)}/history`, T.PublicationHistory),
    publicationAction: async (id, action) => { await request("POST", `/api/publications/${enc(id)}/${action}`, null, {}); },
    reschedulePublication: async (id, at) => {
      const r = await request("POST", `/api/publications/${enc(id)}/reschedule`, T.QueueSaveResult, check(RescheduleRequest, { scheduled_at: at }));
      return { warnings: r.warnings ?? [] };
    },
    publishingSettings: () => request("GET", "/api/settings/publishing", T.PublishingSettings),
    updatePublishingSettings: async (req) => request("PUT", "/api/settings/publishing", T.PublishingSettings, check(UpdatePublishingSettings, req)),
    updatePublicationMetadata: async (id, patch) => { await request("PATCH", `/api/publications/${enc(id)}/metadata`, null, validateOnly(PublicationMetadata.partial(), patch)); },
    createPublishPlan: async (req) => request("POST", "/api/publish-plans", T.PublishPlanResult, check(PublishPlanRequest, req)),
    seriesPlaylists: (id) => request("GET", `/api/series/${enc(id)}/playlists`, T.SeriesPlaylists),
    syncSeriesPlaylist: (id, accountId) => request("POST", `/api/series/${enc(id)}/playlists/sync${q({ account_id: accountId })}`, T.PlaylistSyncResult, {}),

    listAccounts: async () => (await request("GET", "/api/accounts", T.AccountsResponse)).accounts,
    accountImpact: (id) => request("GET", `/api/accounts/${enc(id)}/impact`, T.AccountImpact),
    createAccount: async (req) => request("POST", "/api/accounts", T.Account, check(CreateAccountRequest, req)),
    updateAccount: async (id, req) => { await request("PATCH", `/api/accounts/${enc(id)}`, null, validateOnly(UpdateAccountRequest, req)); },
    disconnectAccount: async (id) => { await request("POST", `/api/accounts/${enc(id)}/disconnect`, null, {}); },
    accountConnectUrl: (id, o) => `${base}/api/accounts/${enc(id)}/connect${o?.confirmChannelChange ? "?confirm_channel_change=1" : ""}`,
    connectAccount: (id, o) => (opts.assign ?? ((u: string) => window.location.assign(u)))(`${base}/api/accounts/${enc(id)}/connect${o?.confirmChannelChange ? "?confirm_channel_change=1" : ""}`),
    youtubeSettings: () => request("GET", "/api/settings/platforms/youtube", T.YoutubeSettings),
    updateYoutubeSettings: async (patch) => request("PUT", "/api/settings/platforms/youtube", T.YoutubeSettings, validateOnly(T.YoutubeSettings.partial(), patch)),

    listQueues: async () => (await request("GET", "/api/queues", T.QueuesResponse)).queues,
    createQueue: async (req) => ({ warnings: (await request("POST", "/api/queues", T.QueueSaveResult, check(UpsertQueueRequest, req))).warnings ?? [] }),
    updateQueue: async (id, req) => ({ warnings: (await request("PUT", `/api/queues/${enc(id)}`, T.QueueSaveResult, check(UpsertQueueRequest, req))).warnings ?? [] }),

    listNotifications: (o = {}) => request("GET", `/api/notifications${q({ unread: o.unread ? "1" : undefined, cursor: o.cursor, limit: o.limit })}`, T.NotificationsPage),
    markNotificationRead: async (id) => { await request("POST", `/api/notifications/${enc(id)}/read`, null, {}); },
    markAllNotificationsRead: async () => { await request("POST", "/api/notifications/read-all", null, {}); },
    notificationSettings: () => request("GET", "/api/settings/notifications", T.NotificationSettings),
    updateNotificationSettings: async (s) => request("PUT", "/api/settings/notifications", T.NotificationSettings, check(NotificationSettings, s)),
    pushPublicKey: async () => (await request("GET", "/api/push/public-key", T.PushKey)).public_key,
    pushSubscribe: async (sub) => { await request("POST", "/api/push/subscriptions", null, check(PushSubscriptionRequest, sub)); },
    pushUnsubscribe: async (endpoint) => { await request("DELETE", "/api/push/subscriptions", null, { endpoint }); },
    pushTest: () => request("POST", "/api/push/test", T.PushTestResult, {}),

    listContacts: async () => (await request("GET", "/api/contacts", T.ContactsResponse)).contacts,
    createContact: async (c) => (await request("POST", "/api/contacts", T.ContactResponse, c)).contact,
    createShareLink: (input) => request("POST", "/api/share-links", T.ShareLinkResponse, input),

    listComments: async (status) => (await request("GET", `/api/comments${q({ status })}`, T.CommentsResponse)).comments,
    draftReply: async (id) => { await request("POST", `/api/comments/${enc(id)}/draft`, null, {}); },
    updateReply: async (id, body) => (await request("PUT", `/api/replies/${enc(id)}`, T.ReplyResponse, { body })).reply,
    replyAction: async (id, action) => { await request("POST", `/api/replies/${enc(id)}/${action}`, null, {}); },

    analyticsOverview: (range, account) => request("GET", `/api/analytics/overview${q({ range, account: account ?? "all" })}`, T.AnalyticsOverview),
    syncAnalytics: (accountId) => request("POST", "/api/analytics/sync", T.AnalyticsSyncResult, accountId ? { account_id: accountId } : {}),
    analyticsRuns: async (o = {}) => (await request("GET", `/api/analytics/runs${q({ account: o.account && o.account !== "all" ? o.account : undefined, limit: o.limit ?? 20 })}`, T.SyncRunsResponse)).runs,
    exportYoutubeData: async ({ account, redact = "none" }) => {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), Math.max(timeoutMs, 60_000));
      let res: Response;
      try {
        res = await doFetch(`${base}/api/youtube-data/export${q({ account, redact })}`, { method: "GET", headers: { accept: "application/json" }, credentials: "include", signal: ctrl.signal });
        if (!res.ok) await handle(res, null); // throws the ApiError
        const blob = await res.blob();
        return { blob, filename: filenameFrom(res.headers.get("content-disposition")) ?? `contentops-youtube-data-${new Date().toISOString().slice(0, 10)}.json` };
      } catch (err) {
        if (err instanceof ApiError) throw err;
        if (ctrl.signal.aborted) throw new ApiError("timeout", "Request timed out", 0);
        throw new ApiError("network", err instanceof Error ? err.message : "Network error", 0);
      } finally {
        clearTimeout(timer);
      }
    },
    deleteYoutubeData: (accountId) => request("DELETE", `/api/youtube-data${q({ account: accountId })}`, T.YoutubeDataDeleted),

    openEvents: (since) => {
      const url = `${base}/api/events${q({ since })}`;
      return opts.eventSource ? opts.eventSource(url) : new EventSource(url, { withCredentials: true });
    },
  };
}

/** `retry_at` from a 429 body: `{ retry_at }`, `{ error: { retry_at } }` or `{ error|details: { details: { retry_at } } }`. */
function retryAtFrom(json: unknown): string | null {
  const o = (v: unknown) => (typeof v === "object" && v ? (v as Record<string, unknown>) : undefined);
  const body = o(json);
  const err = o(body?.error);
  for (const c of [body, err, o(err?.details), o(body?.details)]) if (typeof c?.retry_at === "string") return c.retry_at;
  return null;
}
function retryAfterHeader(v: string | null): string | null {
  if (!v) return null;
  const secs = Number(v);
  if (Number.isFinite(secs)) return new Date(Date.now() + secs * 1000).toISOString();
  const t = Date.parse(v);
  return Number.isNaN(t) ? null : new Date(t).toISOString();
}
function filenameFrom(cd: string | null): string | null {
  if (!cd) return null;
  const star = /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i.exec(cd);
  if (star?.[1]) { try { return decodeURIComponent(star[1].trim().replace(/^"|"$/g, "")); } catch { /* fall through */ } }
  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(cd);
  return plain?.[1]?.trim() || null;
}
